import { useEffect, useState, useMemo } from "react";
import productsData from "../../data/products.json";
import { generateInvoiceNumber } from "../utils/invoiceNumber";
import { calculateItem, calculateTotals } from "../utils/calculations";
import ProductSelector from "../../components/ProductSelector";
import InvoicePreview from "./InvoicePreview";

export default function UkInvoiceForm() {
  const [invoiceNo, setInvoiceNo] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(
    new Date().toISOString().slice(0, 10)
  );

  const [customer, setCustomer] = useState({
    name: "",
    email: "",
    phone: "",
    address: "India",
  });

  // Each item: { id, name, rate, quantity, discount }
  const [items, setItems] = useState([]);

  const [totals, setTotals] = useState({
    amount: 0,
    discount: 0,
    taxableAmount: 0,
    total: 0,
  });

  /* ---------- INVOICE NUMBER ---------- */
  useEffect(() => {
    setInvoiceNo(generateInvoiceNumber());
  }, []);

  /* ---------- CALCULATED ITEMS ---------- */
  const calculatedItems = useMemo(() => {
    return items.map(item => ({
      ...item,
      ...calculateItem(item),
    }));
  }, [items]);

  /* ---------- TOTALS ---------- */
  useEffect(() => {
    setTotals(calculateTotals(calculatedItems));
  }, [calculatedItems]);

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-6xl mx-auto bg-white p-6 rounded shadow">
        <h1 className="text-2xl font-semibold mb-4">
          Create UK Invoice
        </h1>

        {/* Invoice Meta */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium mb-1">
              Invoice No
            </label>
            <input
              type="text"
              value={invoiceNo}
              onChange={(e) =>
                setInvoiceNo(e.target.value)
              }
              className="w-full border p-2 rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Date
            </label>
            <input
              type="date"
              value={invoiceDate}
              onChange={(e) =>
                setInvoiceDate(e.target.value)
              }
              className="w-full border p-2 rounded"
            />
          </div>
        </div>

        {/* Customer Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {[
            ["name", "Customer / Business Name"],
            ["address", "Address"],
          ].map(([key, label]) => (
            <input
              key={key}
              type="text"
              placeholder={label}
              value={customer[key]}
              onChange={(e) =>
                setCustomer({ ...customer, [key]: e.target.value })
              }
              className="border p-2 rounded"
            />
          ))}
        </div>

        {/* Product Selector */}
        <ProductSelector
          products={productsData}
          selectedItems={items}
          setSelectedItems={setItems}
        />

        {/* Items + Discount */}
        {items.length > 0 && (
          <div className="mt-6">
            <h2 className="font-semibold mb-2">Invoice Items</h2>

            <div className="grid grid-cols-6 gap-2 font-medium mb-2">
              <div>Item</div>
              <div>Rate</div>
              <div>Qty</div>
              <div>Amount</div>
              <div>Discount</div>
              <div>Net</div>
            </div>

            {items.map((item, index) => {
              const amount = item.rate * item.quantity;
              const discount = item.discount || 0;

              return (
                <div
                  key={index}
                  className="grid grid-cols-6 gap-2 items-center mb-2"
                >
                  <div>{item.name}</div>
                  <div>Rs. {item.rate}</div>
                  <div>{item.quantity}</div>
                  <div>Rs. {amount.toLocaleString("en-GB")}</div>

                  {/* ✅ Discount Input */}
                  <input
                    type="number"
                    min="0"
                    placeholder="Discount"
                    value={discount}
                    onChange={(e) => {
                      const updated = [...items];
                      updated[index] = {
                        ...updated[index],
                        discount: Number(e.target.value) || 0,
                      };
                      setItems(updated);
                    }}
                    className="border p-1 rounded text-right"
                  />

                  <div className="font-medium">
                    Rs. {(amount - discount).toLocaleString("en-GB")}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Totals */}
        <div className="mt-6 text-right space-y-1">
          <p>Amount: Rs. {totals.amount.toLocaleString("en-GB")}</p>
          <p>Discount: Rs. {totals.discount.toLocaleString("en-GB")}</p>
          <p className="text-lg font-semibold">
            Total: Rs. {totals.total.toLocaleString("en-GB")}
          </p>
        </div>

        {/* PDF Preview */}
        <InvoicePreview
          invoiceNo={invoiceNo}
          invoiceDate={invoiceDate}
          customer={customer}
          items={calculatedItems}
          totals={totals}
        />
      </div>
    </div>
  );
}